import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import { notify } from '../toast';
import { ConfirmDialog } from './ConfirmDialog';

export interface DictionaryDeleteButtonProps {
  /** Удаление разрешено (`can.delete`): кнопка открывает подтверждение. */
  onDelete?: () => void;
  /** Почему удалить нельзя (пользователи на уровне, заявки на награду): неактивная кнопка с подсказкой. */
  disabledReason?: string | null;
}

/**
 * «Удалить» в окне строки справочника (прототип `dictrow`: danger слева от «Отмена»). Без права и без
 * причины (начальный уровень, первая ступень) кнопки нет.
 */
export function DictionaryDeleteButton({ onDelete, disabledReason }: DictionaryDeleteButtonProps) {
  const { t } = useTranslation('dialogs');
  if (onDelete) {
    return (
      <Button variant="danger" onClick={onDelete}>
        {t('dictionary.delete')}
      </Button>
    );
  }
  if (!disabledReason) return null;
  return (
    <Tooltip content={disabledReason}>
      <span className="inline-flex">
        <Button variant="danger" disabled aria-description={disabledReason}>
          {t('dictionary.delete')}
        </Button>
      </span>
    </Tooltip>
  );
}

export interface DictionaryDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Подпись строки («Путешественник», «III Первооткрыватель»). */
  name: string;
  /** Ключ кэша раздела — перечитывается после удаления. */
  queryKey: readonly unknown[];
  onConfirm: () => Promise<unknown>;
}

/**
 * Подтверждение «Удалить» строки справочника (уровни, ступени, награды, точность, специализации):
 * отказ сервера (`409` — строка используется) — его текстом в окне; после удаления — тост «Строка
 * «…» удалена из справочника.».
 */
export function DictionaryDeleteDialog({
  open,
  onOpenChange,
  name,
  queryKey,
  onConfirm,
}: DictionaryDeleteDialogProps) {
  const { t } = useTranslation('dialogs');
  const queryClient = useQueryClient();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      overline={t('dictionary.overline')}
      title={t('dictionary.title', { name })}
      lead={t('dictionary.lead')}
      footNote={t('dictionary.foot')}
      confirmLabel={t('dictionary.delete')}
      confirmVariant="danger"
      onConfirm={async () => {
        await onConfirm();
        void queryClient.invalidateQueries({ queryKey });
        notify(t('dictionary.deleted', { name }));
      }}
    />
  );
}
